import React, { useState, useEffect } from 'react';
import { buildingTypesAPI } from '../../lib/api';
import { Plus, Trash, Pencil, X, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BuildingTypesManager = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ type_code: '', label: '', description: '', display_order: 0 });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const response = await buildingTypesAPI.getBuildingTypes();
      setTypes(response.data);
    } catch (err) {
      toast.error('Failed to load building types');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ type_code: '', label: '', description: '', display_order: 0 });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleAdd = async () => {
    if (!formData.type_code || !formData.label) {
      toast.error('Code and label are required');
      return;
    }
    try {
      await buildingTypesAPI.createBuildingType({
        type_code: formData.type_code,
        label: formData.label,
        description: formData.description,
        display_order: parseInt(formData.display_order) || 0
      });
      toast.success('Building type added');
      resetForm();
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleUpdate = async (typeCode) => {
    try {
      await buildingTypesAPI.updateBuildingType(typeCode, {
        type_code: formData.type_code,
        label: formData.label,
        description: formData.description,
        display_order: parseInt(formData.display_order) || 0
      });
      toast.success('Building type updated');
      resetForm();
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (typeCode) => {
    if (!window.confirm(`Delete building type "${typeCode}"?`)) return;
    try {
      await buildingTypesAPI.deleteBuildingType(typeCode);
      toast.success('Building type deleted');
      fetchTypes();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const startEdit = (type) => {
    setFormData({ ...type });
    setEditingId(type.type_code);
    setShowAddForm(false);
  };

  if (loading) return <div className="text-center py-6 text-sm text-text_muted">Loading...</div>;

  return (
    <section className="bg-white border border-border p-6" data-testid="building-types-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Building Types</h3>
          <p className="text-sm text-text_muted mt-1">Manage the building types available when creating plans.</p>
        </div>
        {!showAddForm && !editingId && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 px-3 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-opacity-90"
            data-testid="add-building-type-btn"
          >
            <Plus size={16} weight="bold" />
            <span>Add Type</span>
          </button>
        )}
      </div>

      {/* Add / Edit Form */}
      {(showAddForm || editingId) && (
        <div className="border border-accent bg-orange-50/40 p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="Code (e.g. A)"
            value={formData.type_code}
            onChange={(e) => setFormData({ ...formData, type_code: e.target.value })}
            className="p-2 border border-border rounded text-sm"
            disabled={!!editingId}
            data-testid="bt-code-input"
          />
          <input
            type="text"
            placeholder="Label (e.g. Type A)"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            className="p-2 border border-border rounded text-sm"
            data-testid="bt-label-input"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="p-2 border border-border rounded text-sm md:col-span-2"
            data-testid="bt-description-input"
          />
          <div className="md:col-span-4 flex justify-end space-x-2">
            <button onClick={resetForm} className="px-3 py-2 border border-border rounded text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => editingId ? handleUpdate(editingId) : handleAdd()}
              className="px-3 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-opacity-90"
              data-testid="save-building-type-btn"
            >
              {editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Types List */}
      <div className="space-y-2">
        {types.map(type => (
          <div key={type.type_code} className="flex items-center justify-between p-3 border border-border" data-testid={`building-type-${type.type_code}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <span className="font-mono font-bold text-primary text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{type.type_code}</span>
                <span className="font-medium">{type.label}</span>
              </div>
              {type.description && <div className="text-xs text-text_muted mt-1">{type.description}</div>}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => startEdit(type)}
                className="p-2 border border-border rounded hover:bg-gray-50"
                data-testid={`edit-bt-${type.type_code}`}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => handleDelete(type.type_code)}
                className="p-2 border border-red-200 rounded text-red-600 hover:bg-red-50"
                data-testid={`delete-bt-${type.type_code}`}
              >
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
        {types.length === 0 && !showAddForm && (
          <p className="text-sm text-text_muted text-center py-4">No building types yet.</p>
        )}
      </div>
    </section>
  );
};

export default BuildingTypesManager;
